import { useEffect, useState } from "react";
import { api, fmtDate, fmtMoney, formatErr } from "../lib/api";
import { Drawer, Field } from "../components/UI";
import { Page } from "../components/AppShell";
import { Receipt, Trash, Link as LinkIcon, ArrowCounterClockwise, ChartBar } from "@phosphor-icons/react";
import useBulkSelect from "../lib/useBulkSelect";

/**
 * Iter 30 — Bills page (inbound contractor invoices).
 *
 * Lists bills the current Core org owes to contractor partners. Each bill
 * is auto-mirrored from a contractor's invoice (via customers.partner_org_id).
 * The Core admin can:
 *   - soft-remove the bill (status=removed, reversible)
 *   - link it to a payment_transactions row (marks paid)
 *   - unlink (back to pending)
 */
export default function Bills() {
  const [bills, setBills] = useState([]);
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [linkBill, setLinkBill] = useState(null);
  const [txnId, setTxnId] = useState("");
  // iter 37 — Bill details drawer (replaces the per-row action buttons).
  const [detailBill, setDetailBill] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/bills?include_removed=${includeRemoved ? "true" : "false"}`)
      .then((r) => setBills(r.data || []))
      .catch((e) => setErr(formatErr(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [includeRemoved]);

  const remove = async (b) => {
    const reason = window.prompt(`Remove bill ${b.bill_number}?\nOptional reason:`, "");
    if (reason === null) return;
    try { await api.delete(`/bills/${b.id}`, { data: { reason: reason || undefined } }); load(); }
    catch (e) { alert(formatErr(e)); }
  };
  const restore = async (b) => {
    try { await api.post(`/bills/${b.id}/restore`); load(); }
    catch (e) { alert(formatErr(e)); }
  };
  const unlink = async (b) => {
    if (!window.confirm(`Unlink transaction from ${b.bill_number}?`)) return;
    try { await api.post(`/bills/${b.id}/unlink-transaction`); load(); }
    catch (e) { alert(formatErr(e)); }
  };
  const submitLink = async () => {
    if (!txnId.trim()) return;
    try {
      await api.post(`/bills/${linkBill.id}/link-transaction`, { transaction_id: txnId.trim() });
      setLinkBill(null); setTxnId(""); load();
    } catch (e) { alert(formatErr(e)); }
  };

  const totals = bills.reduce((acc, b) => {
    if (b.removed_at) return acc;
    acc.pending += b.status === "pending" ? Number(b.amount) : 0;
    acc.paid += b.status === "paid" ? Number(b.amount) : 0;
    return acc;
  }, { pending: 0, paid: 0 });
  const bulk = useBulkSelect(bills, { testid: "bill-row" });

  return (
    <Page
      title="Payables"
      subtitle="FINANCE / INBOUND FROM CONTRACTORS"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Payables"]}
      testid="bills-page"
      actions={
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={includeRemoved}
            onChange={(e) => setIncludeRemoved(e.target.checked)}
            data-testid="bills-include-removed"
          />
          Show removed
        </label>
      }
    >
      <div className="max-w-7xl mx-auto">

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="PENDING"   value={fmtMoney(totals.pending)} testid="bills-stat-pending" tone="amber" />
        <Stat label="PAID"      value={fmtMoney(totals.paid)} testid="bills-stat-paid" tone="green" />
        <Stat label="TOTAL BILLS" value={bills.filter(b => !b.removed_at).length} testid="bills-stat-count" />
      </div>

      {err && <div className="text-xs text-destructive mb-3" data-testid="bills-error">{err}</div>}

      {loading ? (
        <div className="p-12 text-center text-muted-foreground" data-testid="bills-loading">Loading bills…</div>
      ) : bills.length === 0 ? (
        <div className="surface p-12 text-center text-muted-foreground" data-testid="bills-empty">
          <Receipt size={28} weight="thin" className="mx-auto mb-2" />
          No bills yet. When a contractor partner sends you an invoice, it&apos;ll mirror here automatically.
        </div>
      ) : (
        <div className="surface">
          <table className="dense">
            <thead><tr>
              <th className="w-8"><input {...bulk.allProps()} /></th>
              <th>BILL #</th><th>FROM</th><th>AMOUNT</th><th>ISSUED</th><th>DUE</th><th>STATUS</th><th>TRANSACTION</th>
            </tr></thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.id} data-testid={`bill-row-${b.id}`} style={b.removed_at ? { opacity: 0.5 } : undefined}>
                  <td><input {...bulk.rowProps(b.id)} /></td>
                  <td className="text-xs mono">
                    <button className="link-cell" onClick={() => setDetailBill(b)} data-testid={`bill-row-open-${b.id}`} title="Open detailed view">
                      {b.bill_number}
                    </button>
                  </td>
                  <td className="text-sm">{b.from_org_name || b.from_contractor_org_id.slice(0, 8)}</td>
                  <td className="text-xs mono">{fmtMoney(b.amount)}</td>
                  <td className="text-xs text-muted-foreground mono">{fmtDate(b.issue_date)}</td>
                  <td className="text-xs text-muted-foreground mono">{b.due_date ? fmtDate(b.due_date) : "—"}</td>
                  <td className="text-xs"><BillStatus s={b.status} removed={!!b.removed_at} /></td>
                  <td className="text-xs mono text-muted-foreground">
                    {b.linked_transaction_id ? b.linked_transaction_id.slice(0, 12) + "…" : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Drawer open={!!linkBill} onClose={() => setLinkBill(null)} title="Link transaction" size="narrow" testid="bill-link-drawer"
        footer={<><button className="btn btn-ghost" onClick={() => setLinkBill(null)}>CANCEL</button>
                   <button className="btn btn-primary" onClick={submitLink} disabled={!txnId.trim()} data-testid="bill-link-submit"><ChartBar size={14} /> LINK</button></>}
      >
        {linkBill && (
          <div className="space-y-3">
            <div className="text-sm">
              Linking bill <strong>{linkBill.bill_number}</strong> from {linkBill.from_org_name} for {fmtMoney(linkBill.amount)}.
            </div>
            <Field label="TRANSACTION ID">
              <input
                className="surface px-3 py-2 w-full text-sm mono"
                value={txnId}
                onChange={(e) => setTxnId(e.target.value)}
                placeholder="payment_transactions.id"
                data-testid="bill-link-txn-id"
                autoFocus
              />
            </Field>
            <p className="text-xs text-muted-foreground">
              Paste the id from your accounting ledger. The bill will be marked paid and the transaction linked.
            </p>
          </div>
        )}
      </Drawer>

      <Drawer
        open={!!detailBill}
        onClose={() => setDetailBill(null)}
        title={detailBill ? `Bill ${detailBill.bill_number}` : "Bill"}
        size="narrow"
        testid="bill-detail-drawer"
        footer={detailBill && (
          detailBill.removed_at
            ? <button className="btn btn-primary" onClick={() => { restore(detailBill); setDetailBill(null); }} data-testid="bill-detail-restore"><ArrowCounterClockwise size={14} /> RESTORE</button>
            : (
              <>
                {detailBill.linked_transaction_id
                  ? <button className="btn btn-secondary" onClick={() => { unlink(detailBill); setDetailBill(null); }} data-testid="bill-detail-unlink"><LinkIcon size={14} /> UNLINK</button>
                  : <button className="btn btn-secondary" onClick={() => { setLinkBill(detailBill); setTxnId(""); setDetailBill(null); }} data-testid="bill-detail-link"><LinkIcon size={14} /> LINK TXN</button>}
                <button className="btn btn-destructive" onClick={() => { remove(detailBill); setDetailBill(null); }} data-testid="bill-detail-remove"><Trash size={14} /> REMOVE</button>
              </>
            )
        )}
      >
        {detailBill && (
          <div className="space-y-3 text-sm">
            <div><span className="label-xs block">FROM</span>{detailBill.from_org_name || detailBill.from_contractor_org_id}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className="label-xs block">AMOUNT</span><span className="mono">{fmtMoney(detailBill.amount)}</span></div>
              <div><span className="label-xs block">STATUS</span><BillStatus s={detailBill.status} removed={!!detailBill.removed_at} /></div>
              <div><span className="label-xs block">ISSUED</span><span className="mono text-xs">{fmtDate(detailBill.issue_date)}</span></div>
              <div><span className="label-xs block">DUE</span><span className="mono text-xs">{detailBill.due_date ? fmtDate(detailBill.due_date) : "—"}</span></div>
            </div>
            <div>
              <span className="label-xs block">TRANSACTION</span>
              <span className="mono text-xs text-muted-foreground">
                {detailBill.linked_transaction_id || "Not linked"}
              </span>
            </div>
            {detailBill.notes && <div><span className="label-xs block">NOTES</span><p className="text-xs">{detailBill.notes}</p></div>}
          </div>
        )}
      </Drawer>
      </div>
    </Page>
  );
}

const Stat = ({ label, value, testid, tone }) => {
  const color = tone === "green" ? "#16a34a" : tone === "amber" ? "#d97706" : undefined;
  return (
    <div className="surface p-4" data-testid={testid}>
      <div className="text-[10px] mono uppercase text-muted-foreground tracking-widest">{label}</div>
      <div className="text-2xl font-semibold mt-1" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
};

const BillStatus = ({ s, removed }) => {
  if (removed) return <span className="text-muted-foreground">○ removed</span>;
  if (s === "paid")    return <span className="text-green-600 font-medium">● paid</span>;
  if (s === "pending") return <span className="text-amber-600 font-medium">● pending</span>;
  return <span className="text-muted-foreground">○ {s}</span>;
};
